import {
    AnySchema,
    ContractProcedure,
    ContractProcedureBuilderWithInput,
    ContractProcedureBuilderWithInputOutput,
    ContractProcedureBuilderWithOutput,
    ErrorMap,
    Meta
} from "@orpc/contract";

import { contract } from "./contract";

type Primitive = string | number | boolean | bigint | symbol | null | undefined;
// Contracts exported through oc.router(...) (for example validationApi) type their procedures as ContractProcedure.
type OrpcBuilder =
    | ContractProcedure<AnySchema, AnySchema, ErrorMap, Meta>
    | ContractProcedureBuilderWithInput<AnySchema, AnySchema, ErrorMap, Meta>
    | ContractProcedureBuilderWithOutput<AnySchema, AnySchema, ErrorMap, Meta>
    | ContractProcedureBuilderWithInputOutput<AnySchema, AnySchema, ErrorMap, Meta>;

type IsPlainObject<T> = T extends Primitive ? false : T extends OrpcBuilder ? false : T extends Function ? false : T extends object ? true : false;

export type DeepMapLeafArrays<T, V> = IsPlainObject<T> extends true ? { [K in keyof T]?: DeepMapLeafArrays<T[K], V> } : V;

type TimeoutConfig = DeepMapLeafArrays<typeof contract, number>;

// Resource Health's server-side timeout is 30 seconds; allow five seconds for proxy and response overhead.
const resourceHealthTimeout = 35 * 1000;
const customerImpactReportTimeout = 30 * 1000;

// The slow-server entries come from 30 days of Prod data (Oct 2026): only endpoints whose server p95 is over 10 s and
// that still time out, at the smallest of 30 / 60 / 120 s that covers 1.5 × the server p95 (or the server's own limit).
// Timeouts on fast endpoints are client-side and need caller fixes, not a longer timeout here.
const timeoutConfig: TimeoutConfig = {
    emergencyPatchApi: {
        postEmergencyPatchAttempt: 150 * 1000,
        getPendingEmergencyPatchAttempts: 60 * 1000,
        getEmergencyPatchAttempt: 30 * 1000
    },
    ctsEsmIntegrationApi: {
        getEscortRdpLauncherFile: 60 * 1000
    },
    fleetApi: {
        submitSafeguardCheck: 30 * 1000
    },
    shasApi: {
        getGetServiceOwnerInOrgByAlias: 30 * 1000
    },
    runtimeInsightsApi: {
        postDirectReportsAggregatedItemList: 60 * 1000
    },
    opsGenieApi: {
        getOpsGenieResponse: 60 * 1000
    },
    dmsApi: {
        postManagementCmdlet: 30 * 1000,
        // Slow server.
        postManagementWorkflowTrace: 120 * 1000
    },
    // Slow server (all three).
    recoveryApi: {
        postGetRecoveryActionsTrend: 60 * 1000,
        postGetRecoveryActionsHistory: 60 * 1000,
        postGetRecoveryActionsTrendFilterValues: 60 * 1000
    },
    smartActionServiceApi: {
        postCreateNotebookIncidentTrigger: 30 * 1000,
        postExecuteAction: 30 * 1000
    },
    changeInventoryApi: {
        getChanges: 30 * 1000
    },
    deployMetricVersionApi: {
        getDropboxPayload: 60 * 1000
    },
    guardianAIMetricsApi: {
        postCustomerImpactForest: customerImpactReportTimeout,
        postCustomerImpactResidency: customerImpactReportTimeout,
        postCustomerImpactTenantQueues: customerImpactReportTimeout
    },
    oncallApi: {
        // The server responds in under 2 s, but the response is large: on slow connections the download alone takes up to ~35 s (p99).
        getTeamsv2GetTeamServiceTreeMappings: 60 * 1000
    },
    // Carried over from the axios timeout override (Store/ApiOverrides) when ODLPortal moved off Zodios.
    oDLPortalApi: {
        putPutTargetnodeNamesettingName: 60 * 1000
    },
    resourceHealthApi: {
        getResourceMetadata: resourceHealthTimeout,
        getResourceHealthState: resourceHealthTimeout,
        getResourceHealthHistory: resourceHealthTimeout,
        getConnectivity: resourceHealthTimeout,
        getCurrentHealthState: resourceHealthTimeout,
        getEntityAnalyticsTimelines: resourceHealthTimeout,
        getEntityChildren: resourceHealthTimeout,
        getCustomerScenarioCatalog: resourceHealthTimeout,
        getProbeResults: resourceHealthTimeout,
        getCustomerScenarioSuccessRate: resourceHealthTimeout,
        getCustomerScenarioProbeIncidents: resourceHealthTimeout,
        getArchivedUsages: resourceHealthTimeout,
        getArchivedTenantAttributions: resourceHealthTimeout,
        getCustomerScenarioProbeInformation: resourceHealthTimeout
    },
    // Slow servers (Prod data, Oct 2026; rule in the comment above).
    // XamErrorDetails fails on the server about 80% of the time; 30 s is a first step, to be re-checked after deploy.
    healthApi: {
        postXamErrorDetails: 30 * 1000,
        postMachineHealthHeatmapGetMachineHealthHeatmap: 60 * 1000
    },
    passiveMonitorKustoApi: {
        postGetRecoveryLockbox: 30 * 1000,
        postWatsonCrashSummary: 120 * 1000
    },
    whereIsMyFixApi: {
        getChangeListByAlias: 30 * 1000
    },
    // The server stops at about 40 s.
    tenanthealthApi: {
        postGetCustomerCausedIssues: 60 * 1000
    },
    deploymentTrackerApi: {
        getMetricsForStatus: 30 * 1000,
        getParityMetrics: 30 * 1000
    },
    validationApi: {
        getServiceValidationSummary: 60 * 1000
    },
    m365OpsCenterBuildoutApi: {
        getBuildoutManualvalidations: 120 * 1000,
        getBuildoutBuildoutShiproomReport: 30 * 1000
    },
    resilienceHubApi: {
        getServicesserviceIdExperiments: 30 * 1000,
        postDevtoolCosmosdbquery: 30 * 1000
    },
    griffinDeploymentApi: {
        invokeProxyScriptAsync: 30 * 1000
    },
    eLIPApi: {
        getIsExceptionOwner: 30 * 1000,
        getGetInvestigationsOwnedByUser: 120 * 1000
    },
    // PulseIncident's server stops at about 46 s.
    iC3SPoGApi: {
        getPulseIncidentIncidentV2: 60 * 1000,
        getMonthlyReportDailyAggMetricsData: 30 * 1000
    },
    emergencyPatchAgApi: {
        getAirgapList: 30 * 1000
    }
};

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null;
}

/** The timeout for endpoints without their own entry. Only these timeouts are retried (Core/Query/queryRetryPolicy.ts). */
export const defaultRequestTimeoutMs = 15000; // 15 seconds

export function getTimeout(path: readonly string[], timeoutInMilliseconds?: unknown): number {
    if (timeoutInMilliseconds !== undefined) {
        if (
            typeof timeoutInMilliseconds !== "number" ||
            !Number.isInteger(timeoutInMilliseconds) ||
            timeoutInMilliseconds < 1 ||
            timeoutInMilliseconds > 2_147_483_647
        ) {
            throw new RangeError("Request timeout must be an integer between 1 and 2147483647 milliseconds.");
        }
        return timeoutInMilliseconds;
    }

    const defaultTimeout = defaultRequestTimeoutMs;

    let current: Record<string, unknown> = timeoutConfig;
    for (const key of path) {
        if (key in current) {
            const next: unknown = current[key];
            if (typeof next === "number") {
                return next;
            }
            if (isRecord(next)) {
                current = next;
                continue;
            }
        }
        return defaultTimeout;
    }

    return defaultTimeout;
}
